import { Link, useLocation } from "react-router";

import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { registrarWorkspaceSectionForPath, type RegistrarWorkspaceSection } from "@/navigation/sidebar-items";
import { RegistrarWorkspaceFrameContext } from "./use-registrar-workspace-frame";

type RegistrarRoute = {
  label: RegistrarWorkspaceSection;
  url: string;
};

const REGISTRAR_WORKSPACE_ROUTES: readonly RegistrarRoute[] = [
  {
    label: "Overview",
    url: PROTECTED_ROUTES.admin.dashboard,
  },
  {
    label: "Admissions",
    url: PROTECTED_ROUTES.admin.admissions,
  },
  {
    label: "People",
    url: PROTECTED_ROUTES.admin.people,
  },
  {
    label: "Courses",
    url: PROTECTED_ROUTES.admin.courses,
  },
  {
    label: "History",
    url: PROTECTED_ROUTES.admin.courseHistory,
  },
];

/**
 * The page frame of every registrar page (ADR 0029): eyebrow, the one `h1`,
 * one meta line, and the route-backed workspace tabs. Pages under it compose
 * their own local identity as an `h2` through `AcademicPageHeader`.
 */
export function RegistrarWorkspaceFrame({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { name: tenantName } = useTenantPresentation();
  const activeSection = registrarWorkspaceSectionForPath(pathname);

  return (
    <RegistrarWorkspaceFrameContext.Provider value>
      <div className="registrar-workspace">
        <header data-slot="page-frame" className="registrar-workspace__masthead">
          <div className="grid min-w-0 gap-2">
            <p className="eyebrow">{tenantName} · Registrar</p>
            <h1 className="text-title font-semibold text-foreground">
              Registrar Workspace
            </h1>
            <p className="max-w-2xl text-ui text-muted-foreground">
              Manage institutional records from applicant review through closed-term history.
            </p>
          </div>
          <nav className="registrar-workspace__routes" aria-label="Registrar workspace">
            {REGISTRAR_WORKSPACE_ROUTES.map((route) => {
              const active = route.label === activeSection;
              return (
                <Link
                  key={route.url}
                  to={route.url}
                  aria-current={active ? "page" : undefined}
                  className="registrar-workspace__route"
                >
                  {route.label}
                </Link>
              );
            })}
          </nav>
        </header>
        {children}
      </div>
    </RegistrarWorkspaceFrameContext.Provider>
  );
}
